import { Controller } from "@hotwired/stimulus"
import AirDatepicker from "air-datepicker"
import localeEn from "air-datepicker/locale/en"

// Picking a day to READ, which is a navigation rather than a form field.
//
// date_picker_controller does the other job and does it well: it builds a
// visible text input beside a hidden ISO one and writes back on select,
// because everywhere else in the app a date is a value being submitted —
// a weigh-in's date, a date of birth.
//
// Here there is no field and nothing to submit. The trigger is a button and
// selecting a date VISITS that day. Teaching the other controller to be both
// would mean a mode flag on every branch in it; this shares the library, the
// locale and the skin, and diverges only where the jobs actually differ.
//
// NEVER a native <input type="date">. The panel would be drawn by the OS,
// outside the page, where no stylesheet reaches it — the app's oldest
// non-negotiable, and the reason the Air Datepicker skin exists at all.
export default class extends Controller {
  static values = {
    // Where a selection goes. ":date" is replaced with the ISO date, so the
    // route stays in Rails rather than being rebuilt in JavaScript.
    url: String,
    selected: String
  }

  connect() {
    // AN INPUT TO ATTACH TO, because the library decides between floating
    // and inline with `inline || !this.elIsInput` — its own source, and not
    // an option. Given a <button> it renders IN PLACE whatever `container`
    // says, which put a 17.5rem calendar inside a 40px end-cap and stretched
    // the week strip around it.
    //
    // So the picker gets a hidden input pinned to the button's own box, and
    // the button opens it. The input is never typed into and never
    // submitted: it exists so the library has an anchor of the shape it
    // expects.
    this.anchor = document.createElement("input")
    this.anchor.type = "text"
    this.anchor.readOnly = true
    this.anchor.tabIndex = -1
    this.anchor.setAttribute("aria-hidden", "true")
    this.anchor.className = "pointer-events-none absolute inset-0 h-full w-full opacity-0"
    this.anchor.setAttribute("data-1p-ignore", "")
    this.element.classList.add("relative")
    this.element.appendChild(this.anchor)

    this.picker = new AirDatepicker(this.anchor, {
      locale: localeEn,
      autoClose: true,
      selectedDates: this.selectedValue ? [this.selectedValue] : [],

      // Opens below the button and aligned to its right edge, so a picker at
      // the strip's end does not hang off the page.
      position: "bottom right",

      // A readonly input does not open a keyboard, and the calendar is the
      // only way in — which is what we want, since a free-text date is a
      // parsing problem. Same reasoning as date_picker_controller.
      isMobile: false,

      // BRAND, not the skin's body purple. The selected day here is the day
      // you are READING, and every other current-day mark on this page is
      // brand — the body theme is right where the picker names a weigh-in.
      // See DESIGN_SYSTEM.md.
      classes: "thrive-calendar thrive-calendar--brand",

      // ANY DAY. The page renders one day, so the picker picks one day; the
      // week it lands in follows, because the strip is drawn from whatever
      // date is being viewed.
      onSelect: ({ date }) => this.visit(date)
    })

    // OPENED BY THE CLICK, because the library listens for focus on an
    // input and this is a button. Without this the icon did nothing.
    this.onClick = (event) => {
      event.preventDefault()
      this.picker.visible ? this.picker.hide() : this.picker.show()
    }
    this.element.addEventListener("click", this.onClick)
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
    this.picker?.destroy()
    this.anchor?.remove()
  }

  visit(date) {
    if (!date) return

    // Formatted here rather than with toISOString, which converts to UTC —
    // in New Zealand that lands the picker on yesterday for most of the day.
    const iso = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0")
    ].join("-")

    window.location.href = this.urlValue.replace(":date", iso)
  }
}
